import React, { useEffect, useMemo } from 'react';
import { Global, css, ThemeProvider } from '@emotion/react';
import '@reach/skip-nav/styles.css';
import Navigation from './Navigation';
import Cookies from '@components/Cookies';
import VisuallyHidden from './VisuallyHidden';
import { embeddablePaths, pathsWithoutNavigation } from '@config/paths';
import { getPathWithoutLocale, isBrowser, openNewIntercomChat } from '@utils';
import { getCurrentAskParam, getSearchWithoutAskParam } from '@services/intercomUrlService';

import { theme, reset, cookie, swiper } from '@styles';
import { I18nProvider } from '../context/I18nContext';

import 'typeface-ibm-plex-sans';
import 'typeface-poppins';
import { PageProps } from 'gatsby';
import { PageContext } from '@appTypes';
import { NavigationThemeProvider } from '../context/NavigationThemeContext';
import { Head } from './Head';

function shouldShowSiteNavigation(pathname: string, search: string): boolean {
  const urlParams = new URLSearchParams(search);
  const mobileApp = urlParams.get('mobileApp');
  const basePath = getPathWithoutLocale(pathname);
  return !mobileApp && !pathsWithoutNavigation.includes(basePath);
}

/**
 * Intercom is only loaded on non-embeddable paths (see Head). Mirror that gate
 * so the `?ask=` deep link is a no-op exactly where the Messenger is absent.
 */
function isIntercomDeepLinkPath(pathname: string): boolean {
  return !embeddablePaths.includes(getPathWithoutLocale(pathname));
}

const Layout: React.FC<Omit<PageProps<{}, PageContext>, 'children'>> = ({ children, pageContext, location }) => {
  const locale = pageContext?.locale;
  const pathname = location?.pathname ?? '';
  const search = location?.search ?? '';
  const showNavigation = useMemo(
    () => shouldShowSiteNavigation(pathname, search),
    [pathname, search],
  );

  // `?ask=` deep link: open a fresh Intercom conversation, then strip the param
  // so refresh/back will not reopen it. Re-syncs when the query string changes
  // (client navigation), mirroring the `?feature=` handler in features.tsx.
  useEffect(() => {
    if (!isBrowser() || !isIntercomDeepLinkPath(pathname)) return undefined;
    if (!getCurrentAskParam()) return undefined;

    const cancelOpen = openNewIntercomChat();
    const cleanedSearch = getSearchWithoutAskParam(window.location.search);
    const cleanedUrl = `${window.location.pathname}${cleanedSearch}${window.location.hash}`;
    window.history.replaceState(null, '', cleanedUrl);

    return cancelOpen;
  }, [pathname, search]);

  return (
    <I18nProvider locale={locale}>
      <Head />
      <ThemeProvider theme={theme}>
        <Global styles={globalStyle} />
        <NavigationThemeProvider pathname={pathname}>
          <div>
            <VisuallyHidden as='p' data-agent-directive>
              {AGENT_DIRECTIVE}
            </VisuallyHidden>
            {showNavigation ? (
              <Navigation
                navData={pageContext?.mergedNavData}
                footerData={pageContext?.mergedFooterData}
              />
            ) : (
              <></>
            )}
            {children}
          </div>
        </NavigationThemeProvider>
      </ThemeProvider>
      <Cookies />
    </I18nProvider>
  );
};

/** Agent-Friendly Docs Spec (llms-txt-directive-html): a pointer to llms.txt near the top of every page. */
/* One string, so the server HTML holds the sentence in one text node (same words as the .md blockquote). */
const AGENT_DIRECTIVE =
  'AI agents: the site index is at https://hyperhuman.cc/llms.txt. Every page has a Markdown version, for example /pricing.md.';

const globalStyle = css`
  ${swiper}
  ${reset}
  ${cookie}
  html {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  ::selection {
    background-color: ${theme.colors.geyser};
  }
`;

export { Layout };
